import React, {useCallback, useEffect, useState} from "react";
import {
    Card,
    Page,
    Text,
    Link,
    Layout,
    Button,
    ResourceItem,
    ResourceList,
    Banner,
    LegacyStack,
    TextField,
    ResourceListProps,
    Tag, ButtonGroup, Modal, Thumbnail, DropZone, EmptyState, LegacyCard
} from "@shopify/polaris";
import RecommendedApps from "../components/RecommendedApps";
import Alert from "../Lib/Alert";
import Menu from "../Lib/Menu";
import Http from "../Lib/Http";
import {ResourceChart as Chart} from "../types";
import {AppLink, NavigationMenu} from "@shopify/app-bridge/actions";
import {NoteMajor, ImportMinor} from '@shopify/polaris-icons';
import {useGoTo} from "../Lib/useGoTo";
import CriticalError from "@admin/components/CriticalError";


function Dashboard() {
    const dummyChart =
        {
            id: '1',
            title: 'loading',
            tag: 'test',
            created_at: '2024-06-04T12:00:00Z'
        };

    const windowChartCount = window.chart_count && window.chart_count <= 10 ? window.chart_count : 10;

    const dummyCharts: Chart[] = Array.from({ length: windowChartCount }, (_, index) => ({
        ...dummyChart,
        id: (index + 1).toString()  // Ensure each id is unique
    }));

    const [charts, setCharts] = useState<Chart[]>(dummyCharts);
    const [selectedChart, setSelectedChart] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [hasPreviousPage, setHasPreviousPage] = useState(false);
    const [searchInitiated, setSearchInitiated] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | null>(null);
    const [bulkActive, setBulkActive] = useState(false);
    const [bulkError, setBulkError] = useState(false);
    const [chartActive, setChartActive] = useState(false);
    const [showInto, setShowInto] = useState(true);
    const [showBilling, setShowBilling] = useState(true);
    const [file, setFile] = useState<File>();
    const [legacy, setLegacy] = useState<number>(1);
    const [free, setFree] = useState<number>(0);
    const validFileTypes = ['text/txt'];
    const goTo = useGoTo();
    const chartCount = charts.length;

    const handleDropZoneDrop = useCallback(
        (_dropFiles: File[], acceptedFiles: File[], _rejectedFiles: File[]) =>
            setFile(acceptedFiles[0]),
        [],
    );


    const fileUpload = !file && <DropZone.FileUpload />;
    const uploadedFile = file && (
        <LegacyStack>
            <Thumbnail
                size="small"
                alt={file.name}
                source={
                    validFileTypes.includes(file.type)
                        ? window.URL.createObjectURL(file)
                        : NoteMajor
                }
            />
            <div>
                {file.name}{' '}
                <Text variant="bodySm" as="p">
                    {file.size} bytes
                </Text>
            </div>
        </LegacyStack>
    );

    const [selectedItems, setSelectedItems] = useState<
        ResourceListProps['selectedItems']
    >([]);


    useEffect(() => {
        fetchCharts(currentPage, null);
    }, [currentPage]);



    useEffect(() => {
        getShops();
        const isBannerDismissed = localStorage.getItem('intro_dismissed') === 'true';
        setShowInto(!isBannerDismissed);
    }, []);

    useEffect(() => {
        if(showInto){
            localStorage.setItem('intro_dismissed', 'false');
        }else{
            localStorage.setItem('intro_dismissed', 'true');
        }

    }, [showInto]);

    useEffect(() => {
        if(file){
            uploadFile();
        }
    }, [file]);

    useEffect(() => {
        if (free == 1){

            let css = '.Polaris-BlockStack>li:nth-child(1) .Polaris-ActionList__Item,.Polaris-BlockStack>li:nth-child(2) .Polaris-ActionList__Text{color:var(--p-color-text-disabled)!important;}';
            const styleElement = document.createElement('style');
            styleElement.type = 'text/css';
            styleElement.appendChild(document.createTextNode(css));

            window.document.head.appendChild(styleElement);
        }
    }, [free]);




    useEffect(() => {

        if (!searchInitiated){
            return;
        }


        if (searchTimer !== null) {
            clearTimeout(searchTimer); // Clear previous timer
        }

        // Set a new timer to delay the search by 500ms after typing stops
        const timer = setTimeout(() => {
            fetchCharts(1, searchQuery); // Pass the search query
        }, 999);

        // Store the timer in state
        setSearchTimer(timer);
    }, [searchQuery]);

    const fetchCharts = (page:number, query:string | null| undefined) => {
        setLoading(true);
        const queryParams: { page: number; q?: string } = {
            page: page,
        };

        if (query) {
            queryParams.q = query;
        }

        Http.get(`/app/api/v1/charts.json`, { params: queryParams })
            .then((response) => {
                const data = response.data.charts.data;
                setCharts(data);
                setLoading(false);
                setHasNextPage(!!response.data.charts.next_page_url);
                setHasPreviousPage(page > 1);
            })
            .catch((error) => {
                setLoading(false);
                console.error('Error fetching data:', error);
            });
    };

    const uploadFile = () => {
        if (!file) {
            return;
        }
        setBulkError(false);

        Alert.success('Please wait');
        const formData = new FormData();
        formData.append("file", file);

        Http.post(`/app/api/v1/charts/bulk-import`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
            .then((res) => {
                fetchCharts(1,null);
                Alert.success("Bulk charts uploaded");
                setBulkActive(false);
            })
            .catch(() => {
                 setBulkError(true);
                //setBulkActive(false);
            });

    };

    const handleNextPage = () => {
        setCurrentPage(currentPage + 1);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };


    const handleEditClick = (id:string) => {
        goTo('/charts/' + id);
    };
    const preview = (id:string) => {
        goTo('/preview/' + id);
    };

    const handleSearchChange = (value:string) => {
        setSearchQuery(value);
        setSearchInitiated(true);
    };
    const convertTimestamp = (dateString:string) => {
        const datePart = dateString.split(' ')[0];
        return datePart;
    };


    const getShops = () => {
        Http.get(`/app/api/v1/shop.json`)
            .then((response) => {

                let shop = response.data.shop

                if (shop.legacy == 0 && !shop.plan_id) {
                    goTo('/wizard/1')
                    return;
                }

                if (shop.legacy == 0 && shop.plan_id && shop.wizard == 3) {
                    goTo('/wizard/3');
                    return;
                }

                Menu.loadMenu(shop);
                setLegacy(shop.legacy);
                setFree(shop.free);
            })
            .catch((error) => {
                console.error('Error fetching data:', error);
            });
    };


    const duplicate = (chartId : string) => {
        if (legacy == 0 && free == 1 && chartCount >= 3) {
            return;
        }
        setLoading(true);
        Http.post(`/app/api/v1/charts/${chartId}/duplicate.json`).then((res) => {
            Alert.success("Chart duplicated");
            setLoading(false);
            let clonedId = res.data.chart.id;
            goTo('/charts/'+clonedId)
        }).catch((error) => {
            setLoading(false);
        });;
    };

    const newChartAction = () => {
        if (legacy == 0 && free == 1 && chartCount >= 3) {
            setShowBilling(true);
            return;
        }
        setChartActive(true);
    };

    const emptyStateMarkup =
        (!charts.length && searchQuery == '')  ? (
            <EmptyState
                heading="Create a size chart to  get started"
                image="https://cdn.shopify.com/s/files/1/2376/3301/products/emptystate-files.png"
            >
                <p>
                    You can use the button on the right to make a pre-made chart or create your very own
                </p>
            </EmptyState>
        ) : undefined;



    return (
        <Page
            title="ESC Size Charts"
            primaryAction={{
                disabled: (legacy == 0 && free == 1 && chartCount >= 3),
                content: 'Create new chart',
                onAction() {newChartAction()}
            }}
            actionGroups={ [
                {
                    title: 'Other Actions',
                    actions: [
                        {
                            content: 'Bulk import charts',
                            accessibilityLabel: 'Bulk import charts',
                            onAction: () => {setBulkActive(true)},
                        }
                    ],
                },
            ]}
        >
            <Modal
                 open={bulkActive}
                onClose={() =>{ setBulkActive(false)}}
                title="Bulk import"
            >
                <Modal.Section>
                    {free == 0 &&
                        <div>
                            <CriticalError  onDismiss={() => {setBulkError(false)}} showAlert={bulkError} title={'File upload failed'} />
                            <p>
                                You can bulk import multiple size guides directly into to your shop.
                                <br/>
                            </p>
                            <p>
                                <br/>
                                <Link onClick={() => {
                                    goTo('/bulk-help')
                                }}>Click here</Link> for instructions.
                                <br/>
                                <br/>
                            </p>
                            <DropZone allowMultiple={false} onDrop={handleDropZoneDrop}>
                                {uploadedFile}
                                {fileUpload}
                            </DropZone>
                        </div>
                    }

                    {free == 1 &&
                        <div className="nbis-center">
                            You have discovered a premium feature 👀
                            <br/>
                            This feature is only available on the premium plan. Please upgrade to enable this feature'
                            <br/><br/>
                            <Button onClick={() => {goTo('/wizard/2?upgrade=1')}} variant="primary">Upgrade</Button>
                        </div>
                    }

                </Modal.Section>
            </Modal>
            <Modal
                open={chartActive}
                onClose={() => {
                    setChartActive(false)
                }}
                title="Create a new Size Chart"
                primaryAction={{
                    content: 'Create new Chart',
                    onAction: () => {
                        goTo('/charts/new')
                    },
                }}
                secondaryActions={[
                    {
                        content: 'Use pre-made chart',
                        onAction: () => {
                            goTo('/premade')
                        },
                    },
                ]}
            >
                <Modal.Section>
                <div>
                        <p>
                            Using the options below please specify whether you would like to use a pre-made chart or create your very own
                        </p>
                    </div>
                </Modal.Section>
            </Modal>
            <div>
                {(legacy == 0 && free == 1 && chartCount >= 3 && showBilling) &&
                    <Layout>
                        <Layout.Section>
                            <div style={{paddingBottom: '20px', marginBottom: '20px'}}>
                                <Banner
                                    title="Size guide limit reached"
                                    tone="warning"
                                    action={{
                                        content: 'Go to billing', onAction() {
                                            goTo('/wizard/2?upgrade=1');
                                        }
                                    }}
                                >
                                    <p>
                                        You won't be able to add anymore size guides or charts on the free plan.
                                        Upgrade to premium to have unlimited size guides and charts.
                                        <br/>
                                    </p>
                                </Banner>

                            </div>
                        </Layout.Section>
                    </Layout>
                }
                <Layout>
                    {showInto &&
                        <Layout.Section>
                            <LegacyCard sectioned>
                                <div style={{marginTop: '5px', paddingRight: '15px'}}>
                                    <div style={{float: 'right'}}>
                                        <svg onClick={() => {
                                            setShowInto(false)
                                        }} viewBox="0 0 20 20"
                                             style={{
                                                 cursor: 'pointer',
                                                 display: 'block',
                                                 height: '1.25rem',
                                                 width: '1.25rem'
                                             }}
                                             focusable="false"
                                             aria-hidden="true">
                                            <path
                                                d="M13.97 15.03a.75.75 0 1 0 1.06-1.06l-3.97-3.97 3.97-3.97a.75.75 0 0 0-1.06-1.06l-3.97 3.97-3.97-3.97a.75.75 0 0 0-1.06 1.06l3.97 3.97-3.97 3.97a.75.75 0 1 0 1.06 1.06l3.97-3.97 3.97 3.97Z"></path>
                                        </svg>
                                    </div>
                                    <Text variant="headingMd" as="h6">
                                        Hassle-free Size Charts
                                    </Text>
                                    <p className="muted-text-not mt-10">
                                        Welcome to the ESC Size Charts dashboard! Here you can create, view, and manage
                                        your size charts. For seamless display on product pages, ensure the tags
                                        in our app match those on your products.

                                    </p>
                                </div>
                            </LegacyCard>
                        </Layout.Section>
                    }

                    <Layout.Section>
                        <Card>
                            <TextField
                                label=""
                                autoComplete="off"
                                value={searchQuery}
                                onChange={handleSearchChange}
                                placeholder="Search by Size charts"
                            />
                            <ResourceList
                                resourceName={{singular: 'chart', plural: 'charts'}}
                                items={charts}
                                loading={loading}
                                emptyState={emptyStateMarkup}
                                renderItem={(item) => {
                                    const {id, title, tag, created_at} = item;
                                    const tagsArray = tag.split(',').filter(tag => tag.trim() !== '');

                                    return (
                                        <ResourceItem
                                            onClick={() => {
                                                setSelectedChart(id)
                                            }}
                                            id={id}
                                            accessibilityLabel={`View details for ${title}`}
                                            name={title}
                                        >
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}>
                                                <div>
                                                    <Text variant="headingSm" as="h6">{title}</Text>
                                                    <div className='tag-container'>
                                                        {tagsArray.map((tag, index) => (
                                                            <Tag key={index}>{tag.trim()}</Tag>
                                                        ))}
                                                    </div>
                                                </div>
                                                {selectedChart == id &&
                                                    <div>
                                                        <ButtonGroup>
                                                            <Button onClick={() => preview(id)}
                                                            >Preview</Button>
                                                            <Button
                                                                onClick={() => duplicate(id)}
                                                                disabled={(legacy == 0 && free == 1 && chartCount >= 3)}
                                                            >Duplicate</Button>
                                                            <Button onClick={() => handleEditClick(id)}
                                                                    variant="primary">Edit</Button>
                                                        </ButtonGroup>
                                                    </div>
                                                }
                                                {(selectedChart != id && created_at) &&
                                                    <div style={{color: '#808080', fontSize: '0.9em'}}>
                                                        {convertTimestamp(created_at)}
                                                    </div>
                                                }

                                            </div>

                                        </ResourceItem>
                                    );
                                }}
                                pagination={{
                                    hasNext: hasNextPage,
                                    onNext: handleNextPage,
                                    hasPrevious: hasPreviousPage,
                                    onPrevious: handlePreviousPage,
                                }}
                            />
                        </Card>
                    </Layout.Section>
                </Layout>
                <div style={{paddingBottom: '25px'}}></div>
            </div>
        </Page>
    );
}

export default Dashboard;
